import {
  CalendarOutlined,
  DownOutlined,
  FallOutlined,
  InfoCircleOutlined,
  PauseCircleOutlined,
  PieChartOutlined,
  RiseOutlined,
  TableOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import React, { CSSProperties, FC, useCallback, useState } from 'react';

import { useOperationsDashboardContext } from '../../contexts/OperationsDashboardContext';
import {
  computeFlagStatus,
  HealthFlagStatus,
  IHealthFlagRange,
  IHealthFlagResult,
} from '../../utils/healthFlags';
import styles from '../OperationsDashboard/ops-theme.module.css';
import OpsDonorPipelineCards from '../OpsDonorPipelineCards';
import OpsHealthFlagCards from '../OpsHealthFlagCards';
import OpsMetricDetailModal, {
  IOpsMetricDetailConfig,
} from '../OpsMetricDetailModal';
import OpsVolumeActivityCards from '../OpsVolumeActivityCards';

const FLAG_ICONS: Record<string, typeof CalendarOutlined> = {
  newRegistration: CalendarOutlined,
  pipelineStall: PauseCircleOutlined,
  potentialReg: PieChartOutlined,
  unitMom: FallOutlined,
};

function getFlagIcon(flag: IHealthFlagResult): typeof CalendarOutlined {
  if (flag.key === 'unitMom' && flag.status === 'up') return RiseOutlined;
  return FLAG_ICONS[flag.key] || WarningOutlined;
}

export interface ILocationStripEntry {
  locationId: string;
  locationName: string;
  regionName?: string;
  activeDonors: number;
  recruited: number;
  unitsPerDay: number;
  unitsPerDayVsExp?: string;
  pending: number;
  pendingAvgDays?: number;
  sglPercent?: number;
  healthFlags: IHealthFlagResult[];
  attnScore: number;
  lastEvent?: string;
  // Accordion — Volume & Activity
  periodLabel: string;
  dblCount: number;
  sglCount: number;
  successRate?: number;
  attemptedDonations?: number;
  successfulDonations?: number;
  totalUnits?: number;
  donationEventCount?: number;
  prevUnitsPerDay?: number;
  activeDonors90DaysAgo?: number;
  // Accordion — Donor Pipeline
  dblDonorCount: number;
  sglDonorCount: number;
  firstTimeDonationCount: number;
  retiredDonorCount: number;
  unitsPerYear?: number;
  retiringIn30Count?: number;
  retiringIn60Count?: number;
  retiringIn90Count?: number;
  avgDaysAppliedToActive?: number;
  avgDaysPendingToApproved?: number;
  avgDaysActiveToRetired?: number;
  avgDaysPendingToArchived?: number;
}

interface IOpsLocationStripProps {
  entries: ILocationStripEntry[];
  loading?: boolean;
  attnScoreThreshold?: IHealthFlagRange;
  attnScoreMax?: number;
  locationActiveGrowthGoal?: IHealthFlagRange;
  locationPendingThreshold?: IHealthFlagRange;
  onDrilldown?: (
    metricKey: string,
    locationId: string,
    locationName: string
  ) => void;
}

const DOT_CLASS: Record<HealthFlagStatus, string> = {
  up: styles.healthFlagDotUp,
  warn: styles.healthFlagDotWarn,
  down: styles.healthFlagDotDown,
};

const STATUS_COLORS: Record<HealthFlagStatus, string> = {
  up: '#00E5A0',
  warn: '#c6c300',
  down: '#E8008A',
};

const STATUS_COLORS_LIGHT: Record<HealthFlagStatus, string> = {
  up: '#006f4d',
  warn: '#898702',
  down: '#9d005e',
};

function getBorderInfo(flags: IHealthFlagResult[]): {
  style?: CSSProperties;
  className: string;
} {
  const hasDown = flags.some((f) => f.status === 'down');
  if (hasDown) {
    return {
      style: { borderLeftColor: STATUS_COLORS.down },
      className: styles.locationStripWrapper,
    };
  }

  const upCount = flags.filter((f) => f.status === 'up').length;
  const warnCount = flags.filter((f) => f.status === 'warn').length;
  const total = upCount + warnCount;

  if (total === 0 || warnCount === 0) {
    return { className: styles.locationStripWrapper };
  }
  if (upCount === 0) {
    return {
      style: { borderLeftColor: STATUS_COLORS.warn },
      className: styles.locationStripWrapper,
    };
  }

  const greenPct = Math.round((upCount / total) * 100);
  const gradient = `linear-gradient(to bottom, ${STATUS_COLORS.up} 0%, ${
    STATUS_COLORS.up
  } ${Math.max(greenPct - 15, 0)}%, ${STATUS_COLORS.warn} ${Math.min(
    greenPct + 15,
    100
  )}%, ${STATUS_COLORS.warn} 100%)`;
  return {
    style: { '--strip-border-gradient': gradient } as CSSProperties,
    className: `${styles.locationStripWrapper} ${styles.locationStripWrapperGradient}`,
  };
}

/** Build modal configs for each strip cell, scoped to a specific location entry */
function buildCellModal(
  cell:
    | 'activeDonors'
    | 'recruited'
    | 'retiring'
    | 'retired'
    | 'unitsPerDay'
    | 'pending'
    | 'attnScore',
  loc: ILocationStripEntry,
  attnScoreThreshold?: IHealthFlagRange,
  locationActiveGrowthGoal?: IHealthFlagRange,
  attnScoreMax = 20,
  activeDonorStatuses?: string[],
  unitsPerEventDays = 0
): IOpsMetricDetailConfig {
  const retiringTotal =
    (loc.retiringIn30Count ?? 0) +
    (loc.retiringIn60Count ?? 0) +
    (loc.retiringIn90Count ?? 0);

  const sglStatus =
    loc.sglPercent != null
      ? loc.sglPercent >= 28
        ? 'down'
        : loc.sglPercent >= 23
        ? 'warn'
        : 'up'
      : 'up';

  const sglColor =
    sglStatus === 'down'
      ? '#E8008A'
      : sglStatus === 'warn'
      ? '#c6c300'
      : '#00E5A0';

  switch (cell) {
    case 'activeDonors': {
      const prev90 = loc.activeDonors90DaysAgo;
      const growthPct =
        prev90 != null && prev90 > 0
          ? ((loc.activeDonors - prev90) / prev90) * 100
          : undefined;
      const growthStatus =
        growthPct == null
          ? undefined
          : locationActiveGrowthGoal
          ? computeFlagStatus(growthPct, locationActiveGrowthGoal, false)
          : growthPct >= 10
          ? 'up'
          : growthPct >= 1
          ? 'warn'
          : 'down';
      const growthColor =
        growthStatus === 'up'
          ? '#00E5A0'
          : growthStatus === 'warn'
          ? '#c6c300'
          : '#E8008A';
      return {
        title: 'Active Donors',
        description: (
          <>
            <strong>Active Donors</strong> is the count of donors currently
            attributed to <strong>{loc.locationName}</strong> in{' '}
            {(activeDonorStatuses ?? []).join(', ')} status. This is the primary
            capacity indicator for this location.
          </>
        ),
        rows: [
          {
            label: 'Active Donors (now)',
            value: loc.activeDonors,
            bold: true,
            color: '#4B9EFF',
          },
          {
            label: 'Active Donors (90d ago)',
            value: prev90 != null ? prev90 : 'N/A',
          },
          ...(prev90 != null
            ? [
                {
                  label: 'Growth (90d)',
                  value:
                    growthPct == null
                      ? '—'
                      : Math.round(growthPct) === 0
                      ? 'No Change'
                      : `${growthPct >= 0 ? '+' : ''}${growthPct.toFixed(1)}%`,
                  bold: true,
                  color: growthPct != null ? growthColor : undefined,
                },
              ]
            : []),
          {
            label: 'SGL %',
            value: loc.sglPercent != null ? `${loc.sglPercent}%` : '—',
            color: sglColor,
          },
          { label: 'SGL donors', value: loc.sglDonorCount, indent: true },
          { label: 'DBL donors', value: loc.dblDonorCount, indent: true },
        ],
        formula:
          "COUNT(donors WHERE location = this AND status IN ('Active','Approved','AnnualLabworkDue'))",
      };
    }

    case 'recruited':
      return {
        title: 'Recruited',
        description: (
          <>
            <strong>Recruited</strong> counts new donor registrations at{' '}
            <strong>{loc.locationName}</strong> during the selected period (
            {loc.periodLabel}). Consistent recruiting is essential to offset
            age-related attrition and grow the active donor pool.
          </>
        ),
        rows: [
          {
            label: `Recruited (${loc.periodLabel})`,
            value: loc.recruited,
            bold: true,
            color: '#E8008A',
          },
        ],
        formula: `COUNT(donors WHERE location = this AND registration_date WITHIN ${loc.periodLabel})`,
      };

    case 'retiring':
      return {
        title: 'Retiring Donors',
        description: (
          <>
            <strong>Retiring Donors</strong> shows active donors at{' '}
            <strong>{loc.locationName}</strong> who will age out of donation
            eligibility in the next 30, 60, and 90 days. Each one must be
            replaced to maintain collection volume.
          </>
        ),
        rows: [
          {
            label: 'Retiring in 30 days',
            value: loc.retiringIn30Count ?? 0,
            bold: true,
            color: '#E8008A',
          },
          {
            label: 'Retiring in 60 days',
            value: loc.retiringIn60Count ?? 0,
            bold: true,
            color: '#c6c300',
          },
          {
            label: 'Retiring in 90 days',
            value: loc.retiringIn90Count ?? 0,
            bold: true,
            color: '#00E5A0',
          },
          { label: 'Total retiring (90d)', value: retiringTotal, bold: true },
          {
            label: '% of active donors',
            value:
              loc.activeDonors > 0
                ? `${Math.round((retiringTotal / loc.activeDonors) * 100)}%`
                : '—',
            color:
              retiringTotal / Math.max(loc.activeDonors, 1) > 0.1
                ? '#E8008A'
                : '#00E5A0',
          },
        ],
        formula: 'COUNT(donors WHERE age >= eligibility_cutoff - N days)',
      };

    case 'retired':
      return {
        title: 'Retired (Period)',
        description: (
          <>
            <strong>Retired</strong> counts donors at{' '}
            <strong>{loc.locationName}</strong> whose status changed to Retired
            during the selected period ({loc.periodLabel}). Compare against
            Recruited to assess net churn — if retirements consistently exceed
            recruiting, the active pool will shrink.
          </>
        ),
        rows: [
          {
            label: `Retired (${loc.periodLabel})`,
            value: loc.retiredDonorCount,
            bold: true,
            color: '#9B8DB8',
          },
          { label: 'Active donors', value: loc.activeDonors },
          {
            label: 'Churn rate',
            value:
              loc.activeDonors > 0
                ? `${Math.round(
                    (loc.retiredDonorCount / loc.activeDonors) * 100
                  )}%`
                : '—',
          },
        ],
        formula: `COUNT(donors WHERE location = this AND status changed to Retired WITHIN ${loc.periodLabel})`,
      };

    case 'unitsPerDay': {
      const prevUpd = loc.prevUnitsPerDay;
      const updTrendPct =
        prevUpd !== undefined && prevUpd > 0
          ? Math.round(((loc.unitsPerDay - prevUpd) / prevUpd) * 100)
          : undefined;
      return {
        title: 'Units / Event',
        description: (
          <>
            <strong>Units / Event</strong> is the average number of
            non-discarded pRBC units produced per donation event at{' '}
            <strong>{loc.locationName}</strong> during the selected period. A
            donation event is any distinct date on which a donation occurred at
            this location. Higher values indicate a strong DBL donor mix and
            efficient donation sessions.
          </>
        ),
        rows: [
          {
            label: 'Units / Event',
            value: loc.unitsPerDay.toFixed(1),
            bold: true,
            color: '#4B9EFF',
          },
          {
            label: 'Total pRBC Units',
            value: loc.totalUnits ?? '—',
          },
          {
            label: 'Donation Events',
            value: loc.donationEventCount ?? '—',
          },
          ...(prevUpd !== undefined
            ? [
                {
                  label: 'Prior period units / event',
                  value: prevUpd.toFixed(1),
                },
                ...(updTrendPct !== undefined
                  ? [
                      {
                        label: 'Period-over-period',
                        value: `${updTrendPct > 0 ? '+' : ''}${updTrendPct}%`,
                        bold: true,
                        color: updTrendPct >= 0 ? '#00E5A0' : '#E8008A',
                      },
                    ]
                  : []),
              ]
            : []),
        ],
        formula:
          unitsPerEventDays > 0
            ? `Units/Event = Non-Discarded pRBC Units (last ${unitsPerEventDays} days) ÷ (${unitsPerEventDays} ÷ event-frequency days). Event-frequency from ops-dashboard settings (default 29).`
            : 'Units/Event = Non-Discarded pRBC Units ÷ Donation Events (distinct dates with any donation). Window: all time.',
      };
    }

    case 'pending':
      return {
        title: 'Pending Donors',
        description: (
          <>
            <strong>Pending Donors</strong> are donors at{' '}
            <strong>{loc.locationName}</strong> currently in Pending status —
            registered but not yet approved. The sub-value shows average days in
            Pending. High averages indicate a processing or engagement
            bottleneck.
          </>
        ),
        rows: [
          {
            label: 'Pending donors',
            value: loc.pending,
            bold: true,
            color: '#4B9EFF',
          },
          {
            label: 'Avg days in Pending',
            value:
              loc.pendingAvgDays !== undefined ? `${loc.pendingAvgDays}d` : '—',
            bold: true,
            color:
              loc.pendingAvgDays !== undefined
                ? loc.pendingAvgDays >= 60
                  ? '#E8008A'
                  : loc.pendingAvgDays >= 29
                  ? '#c6c300'
                  : '#00E5A0'
                : undefined,
          },
        ],
        formula:
          'AVG(TODAY - date_status_pending) for donors currently in Pending status',
      };

    case 'attnScore':
    default: {
      const attnStatus = attnScoreThreshold
        ? computeFlagStatus(loc.attnScore, attnScoreThreshold, true)
        : 'up';
      const attnColor =
        attnStatus === 'down'
          ? '#E8008A'
          : attnStatus === 'warn'
          ? '#c6c300'
          : '#00E5A0';
      return {
        title: 'Attention Score',
        description: (
          <>
            <strong>Attention Score</strong> is a composite 0–20 metric for{' '}
            <strong>{loc.locationName}</strong> that aggregates health flag
            severity, pipeline stall risk, and retiring donor urgency. Higher
            scores indicate locations requiring more immediate management
            attention.
          </>
        ),
        rows: [
          {
            label: 'Attention Score',
            value: `${loc.attnScore} / ${attnScoreMax}`,
            bold: true,
            color: attnColor,
          },
          {
            label: 'Health flags down',
            value: loc.healthFlags.filter((f) => f.status === 'down').length,
            color: '#E8008A',
          },
          {
            label: 'Health flags warn',
            value: loc.healthFlags.filter((f) => f.status === 'warn').length,
            color: '#c6c300',
          },
          {
            label: 'Health flags good',
            value: loc.healthFlags.filter((f) => f.status === 'up').length,
            color: '#00E5A0',
          },
        ],
        formula:
          'Score = Σ(flag severity weights) + pipeline stall penalty + retiring urgency penalty',
      };
    }
  }
}

const OpsLocationStrip: FC<IOpsLocationStripProps> = ({
  entries,
  loading,
  attnScoreThreshold,
  attnScoreMax = 20,
  locationActiveGrowthGoal,
  locationPendingThreshold,
  onDrilldown,
}) => {
  const { isDarkMode, opsSettings } = useOperationsDashboardContext();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [modalConfig, setModalConfig] = useState<IOpsMetricDetailConfig | null>(
    null
  );

  const toggle = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const openCellModal = (
    cell: Parameters<typeof buildCellModal>[0],
    loc: ILocationStripEntry,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setModalConfig(
      buildCellModal(
        cell,
        loc,
        attnScoreThreshold,
        locationActiveGrowthGoal,
        attnScoreMax,
        opsSettings.activeDonorStatuses,
        opsSettings.unitsPerEventDays ?? 0
      )
    );
  };

  if (loading) {
    return (
      <div className={styles.locationStripList}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className={styles.locationStripWrapper}>
            <div
              className={`${styles.locationStrip} ${styles.locationStripSkeleton}`}
            >
              {Array.from({ length: 6 }).map((__, j) => (
                <div key={j} className={styles.locationStripCell}>
                  <span
                    className={styles.skeletonBar}
                    style={{ width: '60%' }}
                  />
                  <span
                    className={styles.skeletonBar}
                    style={{ width: '40%', height: 20 }}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (entries.length === 0) return null;

  return (
    <>
      <div className={styles.locationStripList}>
        {entries.map((loc, index) => {
          const isOpen = expandedId === loc.locationId;
          const upCount = loc.healthFlags.filter(
            (f) => f.status === 'up'
          ).length;
          const border = getBorderInfo(loc.healthFlags);
          // Active donor growth vs 90 days ago
          const prev90 = loc.activeDonors90DaysAgo;
          const growthPct =
            prev90 != null && prev90 > 0
              ? ((loc.activeDonors - prev90) / prev90) * 100
              : undefined;
          const growthStatus =
            growthPct == null
              ? undefined
              : locationActiveGrowthGoal
              ? computeFlagStatus(growthPct, locationActiveGrowthGoal, false)
              : growthPct >= 10
              ? 'up'
              : growthPct >= 1
              ? 'warn'
              : 'down';
          const statusColors = isDarkMode ? STATUS_COLORS : STATUS_COLORS_LIGHT;
          const growthColor =
            growthStatus != null
              ? statusColors[growthStatus]
              : statusColors.down;
          // Stagger fade-in: cap at 15 items so tail doesn't feel slow
          const staggerDelay = `${Math.min(index, 15) * 40}ms`;
          return (
            <div
              key={loc.locationId}
              className={`${border.className} ${styles.animFadeUpStagger}`}
              style={{ ...border.style, animationDelay: staggerDelay }}
            >
              <div
                className={styles.locationStrip}
                onClick={() => toggle(loc.locationId)}
              >
                {/* Location Name */}
                <div
                  className={`${styles.locationStripCell} ${styles.locationStripName}`}
                >
                  <span className={styles.locationStripNameTitle}>
                    {loc.locationName}
                  </span>
                  <span className={styles.locationStripNameSub}>
                    {loc.regionName || '—'}
                  </span>
                </div>

                {/* Active Donors */}
                <div
                  className={`${styles.locationStripCell} ${styles.cellWithInfoTab}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    gap: 0,
                    minWidth: 140,
                  }}
                >
                  <div
                    style={{
                      flex: '1 1 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      paddingLeft: 6,
                      justifyContent: 'center',
                      gap: 4,
                    }}
                  >
                    <div
                      className={styles.summaryCardInfoTab}
                      onClick={(e) => openCellModal('activeDonors', loc, e)}
                    >
                      <InfoCircleOutlined />
                    </div>
                    <span className={styles.locationStripLabel}>
                      Active Donors
                    </span>
                    <div className={styles.healthFlagCardValueRow}>
                      <span className={styles.locationStripValue}>
                        {loc.activeDonors}
                      </span>
                      <button
                        type="button"
                        className={styles.healthFlagDrilldownBtn}
                        data-drilldown-id={`${loc.locationId}:activeDonors`}
                        title="View in Drilldown"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDrilldown?.(
                            'activeDonors',
                            loc.locationId,
                            loc.locationName
                          );
                        }}
                      >
                        <TableOutlined />
                      </button>
                    </div>
                    <span className={styles.locationStripValueSub}>
                      SGL{' '}
                      <span
                        className={
                          loc.sglPercent != null
                            ? loc.sglPercent >= 28
                              ? styles.locationStripValueRed
                              : loc.sglPercent >= 23
                              ? styles.locationStripValueWarn
                              : styles.locationStripValueGreen
                            : undefined
                        }
                      >
                        {loc.sglPercent != null ? `${loc.sglPercent}%` : '—'}
                      </span>
                    </span>
                  </div>
                  <>
                    <div
                      style={{
                        width: 1,
                        background: isDarkMode
                          ? 'rgba(255,255,255,0.12)'
                          : 'rgba(0,0,0,0.18)',
                        margin: '4px 4px 4px -7px',
                        flexShrink: 0,
                        alignSelf: 'center',
                        height: '60%',
                      }}
                    />
                    <div
                      style={{
                        flex: '0 0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '0 5px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      {growthPct == null ? (
                        <span
                          className={styles.trendNA}
                          style={{
                            color: isDarkMode ? '#F0EBF8' : '#2E1548',
                          }}
                        >
                          N/A
                        </span>
                      ) : Math.round(growthPct) === 0 ? (
                        <>
                          <span className={styles.trendNoChange}>NO</span>
                          <span className={styles.trendNoChange}>CHANGE</span>
                        </>
                      ) : (
                        <>
                          <span
                            className={styles.trendArrow}
                            style={{ color: growthColor }}
                          >
                            {growthPct > 0 ? '▲' : '▼'}
                          </span>
                          <span
                            className={styles.trendPct}
                            style={{ color: growthColor }}
                          >
                            {Math.abs(growthPct) < 1
                              ? `${growthPct.toFixed(1)}%`
                              : `${Math.round(growthPct)}%`}
                          </span>
                        </>
                      )}
                      <span
                        className={styles.trendLabel}
                        style={{
                          color: isDarkMode
                            ? 'rgba(255,255,255,0.45)'
                            : 'rgba(0,0,0,0.45)',
                        }}
                      >
                        90d
                      </span>
                    </div>
                  </>
                </div>

                {/* Recruited */}
                <div
                  className={`${styles.locationStripCell} ${styles.cellWithInfoTab}`}
                >
                  <div
                    className={styles.summaryCardInfoTab}
                    onClick={(e) => openCellModal('recruited', loc, e)}
                  >
                    <InfoCircleOutlined />
                  </div>
                  <span className={styles.locationStripLabel}>Recruited</span>
                  <div className={styles.healthFlagCardValueRow}>
                    <span className={styles.locationStripValue}>
                      {loc.recruited}
                    </span>
                    <button
                      type="button"
                      className={styles.healthFlagDrilldownBtn}
                      data-drilldown-id={`${loc.locationId}:recruited`}
                      title="View in Drilldown"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDrilldown?.(
                          'recruited',
                          loc.locationId,
                          loc.locationName
                        );
                      }}
                    >
                      <TableOutlined />
                    </button>
                  </div>
                  <span className={styles.locationStripValueSub}>
                    this period
                  </span>
                </div>

                {/* Retiring */}
                <div
                  className={`${styles.locationStripCell} ${styles.cellWithInfoTab}`}
                >
                  <div
                    className={styles.summaryCardInfoTab}
                    onClick={(e) => openCellModal('retiring', loc, e)}
                  >
                    <InfoCircleOutlined />
                  </div>
                  <span className={styles.locationStripLabel}>Retiring</span>
                  <span
                    className={`${styles.locationStripValue} ${
                      (loc.retiringIn30Count ?? 0) +
                        (loc.retiringIn60Count ?? 0) +
                        (loc.retiringIn90Count ?? 0) >
                      0
                        ? styles.locationStripValueGreen
                        : ''
                    }`}
                  >
                    {(loc.retiringIn30Count ?? 0) +
                      (loc.retiringIn60Count ?? 0) +
                      (loc.retiringIn90Count ?? 0)}
                  </span>
                  <span className={styles.locationStripValueSub}>
                    next 120d
                  </span>
                </div>

                {/* Retired (period) */}
                <div
                  className={`${styles.locationStripCell} ${styles.cellWithInfoTab}`}
                >
                  <div
                    className={styles.summaryCardInfoTab}
                    onClick={(e) => openCellModal('retired', loc, e)}
                  >
                    <InfoCircleOutlined />
                  </div>
                  <span className={styles.locationStripLabel}>Retired</span>
                  <div className={styles.healthFlagCardValueRow}>
                    <span
                      className={`${styles.locationStripValue} ${
                        loc.retiredDonorCount > 0
                          ? styles.locationStripValueGreen
                          : ''
                      }`}
                    >
                      {loc.retiredDonorCount}
                    </span>
                    <button
                      type="button"
                      className={styles.healthFlagDrilldownBtn}
                      data-drilldown-id={`${loc.locationId}:retired`}
                      title="View retired donors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDrilldown?.(
                          'retired',
                          loc.locationId,
                          loc.locationName
                        );
                      }}
                    >
                      <TableOutlined />
                    </button>
                  </div>
                  <span className={styles.locationStripValueSub}>
                    this period
                  </span>
                </div>

                {/* Units / Event */}
                <div
                  className={`${styles.locationStripCell} ${styles.cellWithInfoTab}`}
                >
                  <div
                    className={styles.summaryCardInfoTab}
                    onClick={(e) => openCellModal('unitsPerDay', loc, e)}
                  >
                    <InfoCircleOutlined />
                  </div>
                  <span className={styles.locationStripLabel}>Units/Event</span>
                  <div className={styles.healthFlagCardValueRow}>
                    <span className={styles.locationStripValue}>
                      {loc.unitsPerDay.toFixed(1)}
                    </span>
                    <button
                      type="button"
                      className={styles.healthFlagDrilldownBtn}
                      data-drilldown-id={`${loc.locationId}:locationUnits`}
                      title="View donor donations"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDrilldown?.(
                          'locationUnits',
                          loc.locationId,
                          loc.locationName
                        );
                      }}
                    >
                      <TableOutlined />
                    </button>
                  </div>
                  <span className={styles.locationStripValueSub}>
                    <span className={styles.locationStripValueGreen}>
                      {loc.unitsPerDayVsExp || '—'}
                    </span>
                  </span>
                </div>

                {/* Pending */}
                <div
                  className={`${styles.locationStripCell} ${styles.cellWithInfoTab}`}
                >
                  <div
                    className={styles.summaryCardInfoTab}
                    onClick={(e) => openCellModal('pending', loc, e)}
                  >
                    <InfoCircleOutlined />
                  </div>
                  <span className={styles.locationStripLabel}>Pending</span>
                  <div className={styles.healthFlagCardValueRow}>
                    <span
                      className={styles.locationStripValue}
                      style={{
                        color: locationPendingThreshold
                          ? statusColors[
                              computeFlagStatus(
                                loc.pending,
                                locationPendingThreshold,
                                true
                              )
                            ]
                          : undefined,
                        opacity: loc.pending === 0 ? 0.5 : undefined,
                      }}
                    >
                      {loc.pending}
                    </span>
                    <button
                      type="button"
                      className={styles.healthFlagDrilldownBtn}
                      data-drilldown-id={`${loc.locationId}:pending`}
                      title="View in Drilldown"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDrilldown?.(
                          'pending',
                          loc.locationId,
                          loc.locationName
                        );
                      }}
                    >
                      <TableOutlined />
                    </button>
                  </div>
                  <span
                    className={`${styles.locationStripValueSub} ${
                      loc.pendingAvgDays !== undefined
                        ? loc.pendingAvgDays >= 60
                          ? styles.locationStripValueRed
                          : loc.pendingAvgDays >= 29
                          ? styles.locationStripValueWarn
                          : styles.locationStripValueGreen
                        : ''
                    }`}
                  >
                    {loc.pendingAvgDays !== undefined
                      ? `avg ${loc.pendingAvgDays}d`
                      : '—'}
                  </span>
                </div>

                {/* Flags */}
                <div className={styles.locationStripCell}>
                  <span className={styles.locationStripLabel}>
                    Flags ({upCount}/{loc.healthFlags.length})
                  </span>
                  <div className={styles.healthFlagDots}>
                    {loc.healthFlags.map((f) => {
                      const Icon = getFlagIcon(f);
                      return (
                        <div
                          key={f.key}
                          className={`${styles.healthFlagDot} ${
                            DOT_CLASS[f.status]
                          }`}
                          title={f.label}
                        >
                          <Icon />
                        </div>
                      );
                    })}
                  </div>
                  <span className={styles.locationStripValueSub}>
                    {loc.lastEvent || '—'}
                  </span>
                </div>

                {/* Attn Score */}
                <div
                  className={`${styles.locationStripCell} ${styles.cellWithInfoTab}`}
                >
                  <div
                    className={styles.summaryCardInfoTab}
                    onClick={(e) => openCellModal('attnScore', loc, e)}
                  >
                    <InfoCircleOutlined />
                  </div>
                  <span className={styles.locationStripLabel}>Attn Score</span>
                  <span className={styles.locationStripValue}>
                    <span
                      style={
                        attnScoreThreshold
                          ? {
                              color:
                                statusColors[
                                  computeFlagStatus(
                                    loc.attnScore,
                                    attnScoreThreshold,
                                    true
                                  )
                                ],
                            }
                          : undefined
                      }
                    >
                      {loc.attnScore}
                    </span>
                    <span style={{ fontSize: '0.75em', opacity: 0.6 }}>
                      /{attnScoreMax}
                    </span>
                  </span>
                </div>

                {/* Expand arrow */}
                <div
                  className={`${styles.locationStripExpand} ${
                    isOpen ? styles.locationStripExpandOpen : ''
                  }`}
                >
                  <DownOutlined />
                </div>
              </div>

              {/* Accordion panel */}
              <div
                className={`${styles.locationStripPanel} ${
                  isOpen ? styles.locationStripPanelOpen : ''
                }`}
              >
                <div className={styles.locationStripPanelInner}>
                  <span className={styles.locationStripPanelTitle}>
                    Health Flags
                  </span>
                  <OpsHealthFlagCards
                    flags={loc.healthFlags}
                    locationId={loc.locationId}
                    onDrilldown={(flagKey) =>
                      onDrilldown?.(flagKey, loc.locationId, loc.locationName)
                    }
                  />

                  <span
                    className={styles.locationStripPanelTitle}
                    style={{ marginTop: 'var(--sp-4)' }}
                  >
                    Volume &amp; Activity
                  </span>
                  <OpsVolumeActivityCards
                    locationId={loc.locationId}
                    periodLabel={loc.periodLabel}
                    recruited={loc.recruited}
                    dblCount={loc.dblCount}
                    sglCount={loc.sglCount}
                    sglPercent={loc.sglPercent}
                    unitsPerDay={loc.unitsPerDay}
                    prevUnitsPerDay={loc.prevUnitsPerDay}
                    successRate={loc.successRate}
                    attemptedDonations={loc.attemptedDonations}
                    successfulDonations={loc.successfulDonations}
                    totalUnits={loc.totalUnits}
                    donationEventCount={loc.donationEventCount}
                    onDrilldown={(key) =>
                      onDrilldown?.(key, loc.locationId, loc.locationName)
                    }
                  />

                  <span
                    className={styles.locationStripPanelTitle}
                    style={{ marginTop: 'var(--sp-4)' }}
                  >
                    Donor Pipeline
                  </span>
                  <OpsDonorPipelineCards
                    locationId={loc.locationId}
                    activeDonors={loc.activeDonors}
                    dblDonorCount={loc.dblDonorCount}
                    sglDonorCount={loc.sglDonorCount}
                    sglPercent={loc.sglPercent}
                    firstTimeDonationCount={loc.firstTimeDonationCount}
                    recruited={loc.recruited}
                    retiredDonorCount={loc.retiredDonorCount}
                    periodLabel={loc.periodLabel}
                    unitsPerYear={loc.unitsPerYear}
                    retiringIn30Count={loc.retiringIn30Count}
                    retiringIn60Count={loc.retiringIn60Count}
                    retiringIn90Count={loc.retiringIn90Count}
                    avgDaysAppliedToActive={loc.avgDaysAppliedToActive}
                    avgDaysPendingToApproved={loc.avgDaysPendingToApproved}
                    avgDaysActiveToRetired={loc.avgDaysActiveToRetired}
                    avgDaysPendingToArchived={loc.avgDaysPendingToArchived}
                    onDrilldown={(key) =>
                      onDrilldown?.(key, loc.locationId, loc.locationName)
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <OpsMetricDetailModal
        visible={modalConfig !== null}
        onClose={() => setModalConfig(null)}
        config={modalConfig}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default OpsLocationStrip;
